import { useEffect, useState } from 'react';
import { Box, Button } from '@mui/material';
import { useAtomValue } from 'jotai';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import * as state from '@/components/Analysis/state';
import { CreateReportModal } from './CreateReportModal';
import { primary } from '@/theme/colors';
import { PointOfInterestGroupBy, GroupByResult } from '@/types';
import { modalStyles } from '../styles';
import { useFeatureFlag } from '@/hooks';
import { ReportInternalType } from '@/__generated__/graphql';
import { useFilterCviAndGviPois } from '@/components/Analysis/ReviewScreen/hooks/useFilterCviAndGviPois';
import { AddToComponentReport } from '@/components/shared/ReportsRevamped/ComponentReports/AddToComponentReport';

export const AddToReportButton = ({
  groupedPois,
}: {
  groupedPois: GroupByResult<PointOfInterestGroupBy>;
}) => {
  const { cviPois, gviPois } = useFilterCviAndGviPois();
  const [createReportModalOpen, setCreateReportModalOpen] = useState(false);
  const allTemplates = useAtomValue(state.allPointOfInterestTemplates);
  const [selectedPointOfInterestIds, setSelectedPointOfInterestIds] = useState<string[]>([]);

  const handleOpen = () => setCreateReportModalOpen(true);
  const handleClose = () => setCreateReportModalOpen(false);

  const isApiInspector = useFeatureFlag(['api-inspector']);

  useEffect(() => {
    if (isApiInspector) {
      const allPois = [...cviPois, ...gviPois];

      setSelectedPointOfInterestIds(allPois.map((poi) => poi.id));
    }
  }, [cviPois, gviPois, isApiInspector]);

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '16px',
        bgcolor: 'white',
      }}
    >
      <Button
        variant="contained"
        onClick={handleOpen}
        sx={{
          maxHeight: '44px',
          width: '100%',
          backgroundColor: primary.darkBlue,
          borderRadius: '2px',
          textTransform: 'none',
          fontSize: '1.5rem',
          '@media (max-width: 1728px)': {
            height: '32px',
          },
        }}
      >
        <CreateNewFolderIcon sx={{ mr: 2 }} />
        Add to Report
      </Button>
      {isApiInspector ? (
        <AddToComponentReport
          title="Create Report"
          open={createReportModalOpen}
          selectedPointOfInterestIds={selectedPointOfInterestIds}
          handleClose={handleClose}
          internalType={ReportInternalType.Component}
        />
      ) : (
        <CreateReportModal
          title="Select relevant points of interest and create your report"
          sx={modalStyles}
          open={createReportModalOpen}
          pointOfInterestTemplates={allTemplates}
          groupedPointOfInterests={groupedPois}
          handleClose={handleClose}
        />
      )}
    </Box>
  );
};
